import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Shield, CalendarClock } from 'lucide-react';
import { Calendar } from '../components/ui/calendar';
import { es } from 'date-fns/locale';
import { DeadlineRow } from '../components/crm/DeadlineRow';
import { BriefDialog } from '../components/crm/BriefDialog';

const toDate = (iso) => { const [y, m, d] = iso.split('-').map(Number); return new Date(y, m - 1, d); };
const iso = (dt) => `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;

export default function DeadlinesCalendarPage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [day, setDay] = useState(null);
  const [drafting, setDrafting] = useState(null);

  useEffect(() => { api.get('/crm/deadlines').then((r) => setItems(r.data.deadlines)); }, [api]);

  const by = (st) => items.filter((d) => d.due_date && st.includes(d.status)).map((d) => toDate(d.due_date));
  const modifiers = useMemo(() => ({ urgent: by(['urgente']), soon: by(['proximo']), ok: by(['vigente']), past: by(['vencido']) }), [items]); // eslint-disable-line react-hooks/exhaustive-deps
  const shown = day ? items.filter((d) => d.due_date === iso(day)) : items.filter((d) => d.status !== 'vencido');

  return (
    <div data-testid="deadlines-calendar-page" className="min-h-screen bg-slate-950 text-white">
      <header className="border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center gap-3">
          <button data-testid="deadlines-back-btn" onClick={() => navigate('/crm')}><Shield className="w-8 h-8 text-blue-500" /></button>
          <span className="text-xl font-bold">Calendario de Plazos</span>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid lg:grid-cols-[auto_1fr] gap-8 [&>*]:min-w-0">
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-2 sm:p-4 self-start max-w-full overflow-x-auto">
          <Calendar
            data-testid="deadlines-calendar"
            mode="single" locale={es} selected={day} onSelect={setDay}
            modifiers={modifiers}
            modifiersClassNames={{ urgent: '!bg-red-500/30 !text-red-200 font-bold', soon: '!bg-amber-500/30 !text-amber-200 font-bold', ok: '!bg-emerald-500/45 !text-white font-bold', past: 'line-through opacity-60' }}
          />
          <div className="flex flex-wrap gap-3 text-[11px] text-slate-400 px-3 pb-2">
            <span className="flex items-center gap-1"><i className="w-2.5 h-2.5 rounded-full bg-red-500" />≤ 3 días</span>
            <span className="flex items-center gap-1"><i className="w-2.5 h-2.5 rounded-full bg-amber-500" />≤ 7 días</span>
            <span className="flex items-center gap-1"><i className="w-2.5 h-2.5 rounded-full bg-emerald-500" />vigente</span>
          </div>
        </div>
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold flex items-center gap-2"><CalendarClock className="w-5 h-5 text-amber-400" />{day ? `Plazos del ${iso(day).split('-').reverse().join('-')}` : 'Próximos plazos'}</h2>
            {day && <button data-testid="deadlines-clear-day" onClick={() => setDay(null)} className="text-xs text-amber-400 underline">Ver todos</button>}
          </div>
          <div data-testid="deadlines-upcoming-list" className="space-y-2">
            {shown.length === 0 && <p className="text-sm text-slate-500">Sin plazos. Abre un caso del CRM y pulsa «Plazos» para detectarlos o calcularlos.</p>}
            {shown.map((d) => <DeadlineRow key={d.id} d={d} showMatter onDraft={setDrafting} />)}
          </div>
        </section>
      </main>
      {drafting && <BriefDialog matter={{ id: drafting.matter_id, title: drafting.matter_title, country: drafting.matter_country }} deadline={drafting} open={!!drafting} onOpenChange={(o) => !o && setDrafting(null)} />}
    </div>
  );
}
